import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Image as ImageIcon, Megaphone, Lock, Pause } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { EmbeddedYap } from './EmbeddedYap';
import { useUserData } from './UserDataContext';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { VoiceRecorderUI } from './VoiceRecorderUI';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { StorageService } from '../services/StorageService';

interface ShoutYapModalProps {
  isOpen: boolean;
  onClose: () => void;
  yap: any;
  onShoutWithCaption: (caption: string, images: string[], friendsOnly: boolean, audioData?: { url: string, duration: number }) => void;
  onShoutWithoutCaption: () => void;
}

export function ShoutYapModal({ isOpen, onClose, yap, onShoutWithCaption, onShoutWithoutCaption }: ShoutYapModalProps) {
  const [caption, setCaption] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [friendsOnly, setFriendsOnly] = useState(false);
  const { userProfile } = useUserData();
  const [mounted, setMounted] = useState(false);

  // 🎙️ VOICE RECORDING STATE
  const { isRecording, isPaused, duration, startRecording, stopRecording, pauseRecording, resumeRecording, audioBlob, reset, error } = useVoiceRecorder(10000);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [voicePreviewUrl, setVoicePreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!audioBlob || isRecording) return;
    const url = URL.createObjectURL(audioBlob);
    setVoicePreviewUrl(url);
    setVoiceBlob(audioBlob);
  }, [audioBlob, isRecording]);

  useEffect(() => {
    return () => {
      if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
    };
  }, [voicePreviewUrl]);

  const handleAddImage = () => {
    if (images.length >= 2) {
      toast.error('Maximum 2 images allowed', { duration: 1500 });
      return;
    }
    toast.info('Image selection will be implemented soon');
  };

  const handleRemoveImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleShoutWithCaption = async () => {
    if (caption.trim() || images.length > 0 || voiceBlob) {
      if (caption.length > 300) {
        toast.error('Caption is too long. Maximum 300 characters allowed.', { duration: 1500 });
        return;
      }

      const loadingToast = toast.loading('Posting shout...');
      try {
        let audioData = undefined;
        if (voiceBlob) {
          const userIdForStorage = userProfile?.id;
          if (userIdForStorage) {
            const url = await StorageService.uploadVoiceReply(String(userIdForStorage), voiceBlob);
            audioData = { url, duration };
          }
        }

        onShoutWithCaption(caption, images, friendsOnly, audioData);
        toast.dismiss(loadingToast);
        handleClose();
      } catch (err) {
        toast.dismiss(loadingToast);
        toast.error('Failed to post shout. Please try again.');
      }
    } else {
      onShoutWithoutCaption();
      handleClose();
    }
  };

  const handleClose = () => {
    setCaption('');
    setImages([]);
    setFriendsOnly(false);
    setVoiceBlob(null);
    if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
    setVoicePreviewUrl(null);
    reset();
    onClose();
  };

  if (!yap || !mounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-9999 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-light-pattern dark:bg-[#121212] rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col border border-black/10 dark:border-[#4478ea]/30 modal-shadow"
          >
            {/* Header — fixed */}
            <div className="shrink-0 px-6 pt-6 pb-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl text-[#ea580c] dark:text-white font-bold">
                    Shout This Yap
                  </h2>
                  <p className="text-sm text-[#373737] dark:text-[#E5E5E5]">
                    {friendsOnly ? '👥 Visible to friends only' : '🌍 Visible to everyone'}
                  </p>
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleClose}
                  className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-[#ea580c] dark:text-white" />
                </motion.button>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 space-y-6">
              {/* Original Yap Preview */}
              <div className="mb-2">
                <EmbeddedYap
                  yapId={yap.id}
                  username={String(yap.username || yap.userUsername || (typeof yap.author === 'object' && yap.author?.username ? yap.author.username : typeof yap.author === 'string' ? yap.author : 'unknown'))}
                  authorName={String((typeof yap.author === 'object' && yap.author?.name ? yap.author.name : typeof yap.author === 'string' ? yap.author : yap.userName || 'Unknown'))}
                  avatar={String((typeof yap.author === 'object' && yap.author?.avatar ? yap.author.avatar : yap.avatar || yap.userAvatar || ''))}
                  timestamp={String(yap.timestamp || yap.time || 'Just now')}
                  content={String(yap.content || '')}
                  image={yap.image || (yap.images && yap.images[0])}
                  country={yap.country}
                  likes={yap.likes || 0}
                  comments={yap.replies || yap.comments || 0}
                  shouts={yap.shouts || 0}
                  isDeleted={yap.deleted}
                  maxLines={3}
                  minimal={true}
                  onYapClick={() => {}}
                />
              </div>

              {/* Caption Input */}
              <div className="space-y-4">
                <Textarea
                  value={caption}
                  onChange={(e) => {
                    if (e.target.value.length <= 300) {
                      setCaption(e.target.value);
                    }
                  }}
                  placeholder={voicePreviewUrl ? "Add a caption to your voice shout..." : "Shout your thoughts to the world..."}
                  className="w-full min-h-[120px] bg-black/5 dark:bg-[#1E1E1E] border border-black/10 dark:border-white/10 text-black dark:text-white placeholder:text-black/40 dark:placeholder:text-white/30 resize-none rounded-2xl p-4 focus:ring-2 focus:ring-[#4478ea]/50 transition-all text-[15px] outline-none whitespace-pre-wrap"
                />

                {/* 🎙️ VOICE PREVIEW PLAYER */}
                {voicePreviewUrl && (
                  <div className="mt-2 relative group w-fit">
                    <VoiceReplyBubble 
                      audioUrl={voicePreviewUrl} 
                      duration={duration} 
                      className="pr-10" // Space for the discard button
                    />
                    <button
                      onClick={() => {
                        if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
                        setVoicePreviewUrl(null);
                        setVoiceBlob(null);
                        reset(); // Also reset the hook
                      }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors z-10"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="flex justify-start">
                  <p className="text-xs font-medium text-[#666666] dark:text-white/40">
                    {caption.length} / 300 characters
                  </p>
                </div>
              </div>

              {/* Friends Only Toggle */}
              <div className="p-4 rounded-2xl bg-black/5 dark:bg-[#1E1E1E] border border-black/10 dark:border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E74C3C]/10 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-[#E74C3C]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#ea580c] dark:text-white">Friends Only</p>
                    <p className="text-[10px] text-[#666666] dark:text-[#E5E5E5]">Only friends see this shout</p>
                  </div>
                </div>
                <Checkbox
                  id="friends-only"
                  checked={friendsOnly}
                  onCheckedChange={(checked: boolean | string) => setFriendsOnly(!!checked)}
                  className="w-6 h-6 border-black/20 dark:border-white/20 data-[state=checked]:bg-[#E74C3C] data-[state=checked]:border-[#E74C3C]"
                />
              </div>

              {/* Display Added Images */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 gap-4 pb-4">
                  {images.map((image, index) => (
                    <div key={index} className="relative rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 group bg-black/5 dark:bg-white/5">
                      <img
                        src={image}
                        alt={`Added Image ${index + 1}`}
                        className="w-full h-auto object-cover"
                      />
                      <button
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Bar — fixed */}
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-black/10 dark:border-white/20 bg-light-pattern dark:bg-[#1a1a1a] rounded-b-3xl">
              <div className="flex items-center gap-2">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAddImage}
                  disabled={images.length >= 2 || isRecording}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${images.length < 2 && !isRecording
                    ? 'bg-black/5 dark:bg-slate-800 text-[#ea580c] dark:text-[#4478ea] hover:bg-black/10 dark:hover:bg-slate-700'
                    : 'bg-gray-100 dark:bg-slate-900 text-[#999999] cursor-not-allowed'
                    }`}
                >
                  <ImageIcon className="w-5 h-5" />
                  <span className="text-sm hidden sm:inline">
                    Image ({images.length}/2)
                  </span>
                </motion.button>

                {!voicePreviewUrl && (
                  <VoiceRecorderUI 
                    isRecording={isRecording}
                    isPaused={isPaused} // ⏸️ Added
                    duration={duration}
                    maxDuration={10000}
                    onStart={startRecording}
                    onStop={stopRecording}
                    onCancel={reset}
                    onPause={pauseRecording}   // ⏸️ Added
                    onResume={resumeRecording} // ⏸️ Added
                  />
                )}
              </div>

              <Button
                variant="ghost"
                onClick={async () => {
                  if (isRecording) {
                    stopRecording();
                    return;
                  }
                  await handleShoutWithCaption();
                }}
                className="bg-brand-gradient hover:opacity-90 text-white px-8 py-2 rounded-xl font-bold transition-all active:scale-95 shadow-lg flex items-center gap-2"
              >
                {isRecording ? <Pause className="w-4 h-4" /> : <Megaphone className="w-4 h-4" />}
                {isRecording ? 'Stop' : (caption.trim() || images.length > 0 || voiceBlob ? 'Shout' : 'Quick Shout')}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
